// Kraken Public API utilities
const KRAKEN_API_BASE = 'https://api.kraken.com/0/public'

export interface TickerData {
  price: number
  change: number
  changePercent: number
  volume: number
  high: number
  low: number
}

export interface OrderBookData {
  bids: Array<[string, string, string]>
  asks: Array<[string, string, string]>
}

export interface OHLCData {
  time: number
  open: number
  high: number
  low: number
  close: number
  volume: number
}

export interface TradeData {
  price: number
  volume: number
  time: number
  side: 'buy' | 'sell'
}

// Fetch ticker data for a trading pair
export async function fetchTicker(
  pair: string = 'XBTUSD',
): Promise<TickerData> {
  try {
    const response = await fetch(`${KRAKEN_API_BASE}/Ticker?pair=${pair}`)
    const data = await response.json()

    if (data.error && data.error.length > 0) {
      throw new Error(data.error[0])
    }

    const ticker = data.result[Object.keys(data.result)[0]]
    const lastPrice = parseFloat(ticker.c[0])
    const openPrice = parseFloat(ticker.o)
    const change = lastPrice - openPrice
    const changePercent = (change / openPrice) * 100

    return {
      price: lastPrice,
      change,
      changePercent,
      volume: parseFloat(ticker.v[1]),
      high: parseFloat(ticker.h[1]),
      low: parseFloat(ticker.l[1]),
    }
  } catch (error) {
    console.error('Error fetching ticker:', error)
    throw error
  }
}

// Fetch order book data
export async function fetchOrderBook(
  pair: string = 'XBTUSD',
  count: number = 25,
): Promise<OrderBookData> {
  try {
    const response = await fetch(
      `${KRAKEN_API_BASE}/Depth?pair=${pair}&count=${count}`,
    )
    const data = await response.json()

    if (data.error && data.error.length > 0) {
      throw new Error(data.error[0])
    }

    const orderBook = data.result[Object.keys(data.result)[0]]

    return {
      bids: orderBook.bids,
      asks: orderBook.asks,
    }
  } catch (error) {
    console.error('Error fetching order book:', error)
    throw error
  }
}

// Fetch recent trades
export async function fetchTrades(
  pair: string = 'XBTUSD',
  since?: number,
): Promise<TradeData[]> {
  try {
    const url = since
      ? `${KRAKEN_API_BASE}/Trades?pair=${pair}&since=${since}`
      : `${KRAKEN_API_BASE}/Trades?pair=${pair}`

    const response = await fetch(url)
    const data = await response.json()

    if (data.error && data.error.length > 0) {
      throw new Error(data.error[0])
    }

    const trades = data.result[Object.keys(data.result)[0]]

    return trades.map((trade: any[]) => ({
      price: parseFloat(trade[0]),
      volume: parseFloat(trade[1]),
      time: trade[2] * 1000, // Convert to milliseconds
      side: trade[3] === 'b' ? 'buy' : 'sell',
    }))
  } catch (error) {
    console.error('Error fetching trades:', error)
    throw error
  }
}

// Fetch OHLC (candlestick) data
export async function fetchOHLC(
  pair: string = 'XBTUSD',
  interval: number = 60,
): Promise<OHLCData[]> {
  try {
    const response = await fetch(
      `${KRAKEN_API_BASE}/OHLC?pair=${pair}&interval=${interval}`,
    )
    const data = await response.json()

    if (data.error && data.error.length > 0) {
      throw new Error(data.error[0])
    }

    const ohlcData = data.result[Object.keys(data.result)[0]]

    return ohlcData.map((candle: any[]) => ({
      time: candle[0] * 1000,
      open: parseFloat(candle[1]),
      high: parseFloat(candle[2]),
      low: parseFloat(candle[3]),
      close: parseFloat(candle[4]),
      volume: parseFloat(candle[6]),
    }))
  } catch (error) {
    console.error('Error fetching OHLC:', error)
    throw error
  }
}

// Format order book entries for display
export function formatOrderBookEntry(entry: [string, string, string]) {
  return {
    price: parseFloat(entry[0]),
    quantity: parseFloat(entry[1]),
    total: parseFloat(entry[0]) * parseFloat(entry[1]),
  }
}
